export type GrupoPlazoInvalidacion = 'decimoDiaHabil' | 'tresMeses'

export interface PlazoInvalidacion {
  grupo: GrupoPlazoInvalidacion
  desde: Date | null
  hasta: Date | null
  cumple: boolean
  mensaje: string
}

export const TIPOS_DTE_PLAZO_TRES_MESES: ReadonlyArray<string> = ['01', '11', '14']

export const MESES_PLAZO_TRES_MESES = 3

export const DIAS_HABILES_PLAZO_MES_SIGUIENTE = 10

export const AVISO_PLAZO_TRANSMISION_INFORMATIVO =
  'Este plazo es informativo y no le impide transmitir: el Manual Funcional de la versión 2 se contradice en sus propios ejemplos del décimo día hábil, así que la plataforma no lo bloquea. La Fecha del Evento sí es exacta y Hacienda la rechaza si no coincide.'

export const grupoPlazoInvalidacion = (tipoDte: any): GrupoPlazoInvalidacion =>
  TIPOS_DTE_PLAZO_TRES_MESES.indexOf(`${tipoDte ?? ''}`.trim()) >= 0
    ? 'tresMeses'
    : 'decimoDiaHabil'

export const fechaDesdeTexto = (valor: any): Date | null => {
  const texto = `${valor ?? ''}`.trim()
  if (texto.length < 10) return null

  const partes = texto.substring(0, 10).split('-')
  if (partes.length !== 3) return null

  const anio = Number(partes[0])
  const mes = Number(partes[1])
  const dia = Number(partes[2])
  if (!anio || !mes || !dia) return null

  const fecha = new Date(anio, mes - 1, dia)
  if (Number.isNaN(fecha.getTime())) return null
  if (fecha.getMonth() !== mes - 1 || fecha.getDate() !== dia) return null

  return fecha
}

export const soloFecha = (fecha: Date): Date =>
  new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate())

export const formatearFechaPlazo = (fecha: Date | null): string => {
  if (!fecha) return ''
  const dia = `${fecha.getDate()}`.padStart(2, '0')
  const mes = `${fecha.getMonth() + 1}`.padStart(2, '0')
  return `${dia}/${mes}/${fecha.getFullYear()}`
}

export const sumarMeses = (fecha: Date, meses: number): Date => {
  const objetivo = new Date(fecha.getFullYear(), fecha.getMonth() + meses, 1)
  const ultimoDia = new Date(objetivo.getFullYear(), objetivo.getMonth() + 1, 0).getDate()
  const dia = fecha.getDate() < ultimoDia ? fecha.getDate() : ultimoDia
  return new Date(objetivo.getFullYear(), objetivo.getMonth(), dia)
}

const esDiaHabil = (fecha: Date, asuetos: ReadonlyArray<Date>): boolean => {
  const dia = fecha.getDay()
  if (dia === 0 || dia === 6) return false
  return !asuetos.some((asueto) => soloFecha(asueto).getTime() === fecha.getTime())
}

export const nDiaHabil = (
  primerDiaDelMes: Date,
  cantidad: number,
  asuetos: ReadonlyArray<Date> = []
): Date => {
  let dia = soloFecha(primerDiaDelMes)
  let habiles = 0

  while (habiles < cantidad) {
    if (esDiaHabil(dia, asuetos)) habiles += 1
    if (habiles < cantidad) dia = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate() + 1)
  }

  return dia
}

export const decimoDiaHabilDelMesSiguiente = (
  fechaTransmisionDte: Date,
  asuetos: ReadonlyArray<Date> = []
): Date =>
  nDiaHabil(
    new Date(fechaTransmisionDte.getFullYear(), fechaTransmisionDte.getMonth() + 1, 1),
    DIAS_HABILES_PLAZO_MES_SIGUIENTE,
    asuetos
  )

export const validarFechaDelEvento = (
  tipoDte: any,
  fechaGeneracion: Date | null,
  fechaDelEvento: Date | null
): PlazoInvalidacion => {
  const grupo = grupoPlazoInvalidacion(tipoDte)

  if (!fechaGeneracion || !fechaDelEvento) {
    return {
      grupo,
      desde: null,
      hasta: null,
      cumple: false,
      mensaje: 'No se logró leer la fecha de generación del documento.',
    }
  }

  const generacion = soloFecha(fechaGeneracion)
  const evento = soloFecha(fechaDelEvento)

  if (grupo === 'tresMeses') {
    const hasta = sumarMeses(generacion, MESES_PLAZO_TRES_MESES)
    const cumple = evento.getTime() >= generacion.getTime() && evento.getTime() <= hasta.getTime()

    return {
      grupo,
      desde: generacion,
      hasta,
      cumple,
      mensaje: cumple
        ? ''
        : `La Fecha del Evento debe estar entre el ${formatearFechaPlazo(
            generacion
          )} y el ${formatearFechaPlazo(
            hasta
          )}, que son la fecha de generación del documento y tres meses después.`,
    }
  }

  const cumple = evento.getTime() === generacion.getTime()

  return {
    grupo,
    desde: generacion,
    hasta: generacion,
    cumple,
    mensaje: cumple
      ? ''
      : `La Fecha del Evento debe ser la misma fecha de generación del documento que va a invalidar (${formatearFechaPlazo(
          generacion
        )}).`,
  }
}

export const calcularPlazoDeTransmision = (
  tipoDte: any,
  fechaTransmisionDte: Date | null,
  fechaDelDia: Date | null,
  asuetos: ReadonlyArray<Date> = []
): PlazoInvalidacion => {
  const grupo = grupoPlazoInvalidacion(tipoDte)

  if (!fechaTransmisionDte) {
    return {
      grupo,
      desde: null,
      hasta: null,
      cumple: true,
      mensaje: '',
    }
  }

  const transmision = soloFecha(fechaTransmisionDte)
  const hasta =
    grupo === 'tresMeses'
      ? sumarMeses(transmision, MESES_PLAZO_TRES_MESES)
      : decimoDiaHabilDelMesSiguiente(transmision, asuetos)

  if (!fechaDelDia) {
    return {grupo, desde: transmision, hasta, cumple: true, mensaje: ''}
  }

  const dia = soloFecha(fechaDelDia)
  const cumple = dia.getTime() >= transmision.getTime() && dia.getTime() <= hasta.getTime()

  return {
    grupo,
    desde: transmision,
    hasta,
    cumple,
    mensaje: cumple
      ? ''
      : `El plazo para transmitir la invalidación venció el ${formatearFechaPlazo(hasta)}.`,
  }
}

export const descripcionFechaDelEvento = (tipoDte: any, fechaGeneracion: Date | null): string => {
  if (!fechaGeneracion) return 'No se logró leer la fecha de generación del documento.'

  const generacion = soloFecha(fechaGeneracion)

  if (grupoPlazoInvalidacion(tipoDte) === 'tresMeses') {
    const hasta = sumarMeses(generacion, MESES_PLAZO_TRES_MESES)
    return `La Fecha del Evento puede ir del ${formatearFechaPlazo(
      generacion
    )} al ${formatearFechaPlazo(hasta)}; la plataforma envía el ${formatearFechaPlazo(generacion)}.`
  }

  return `La Fecha del Evento tiene que ser exactamente el ${formatearFechaPlazo(
    generacion
  )}, la fecha en que se generó el documento; es la que envía la plataforma.`
}

export const descripcionPlazoDeTransmision = (
  tipoDte: any,
  fechaTransmisionDte: Date | null,
  fechaDelDia: Date | null,
  asuetos: ReadonlyArray<Date> = []
): string => {
  const plazo = calcularPlazoDeTransmision(tipoDte, fechaTransmisionDte, fechaDelDia, asuetos)
  if (!plazo.hasta) return ''

  const fin = formatearFechaPlazo(plazo.hasta)
  const regla =
    plazo.grupo === 'tresMeses'
      ? 'tres meses contados desde el sello de recepción'
      : 'el décimo día hábil del mes siguiente al sello de recepción'

  if (!plazo.cumple) {
    return `El plazo para transmitir la invalidación venció el ${fin} (${regla}).`
  }

  return `Tiene plazo para transmitir la invalidación hasta el ${fin} (${regla}).`
}
